import { i18nDynamic } from 'shared/i18n';
import { useDocumentParams } from 'app/router/params';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, ChangeEvent } from 'react';
import { useDocumentMetadataSave, useGetDocument } from 'entities/document';
import { LanguageSelector } from 'app/components';
import { useUser } from 'app/state/contexts';
import { useDelayedSave } from 'app/utils/hooks/delayed-save';
import { langs } from 'shared/forms/LanguageSelector';

const defaultTexts = { text1: '', text2: '', text3: '', text4: '', text5: '', text6: '', text7: '', text8: '', text9: '' };

const Metadata = () => {
  const { t } = useTranslation('project-details');

  const { documentId } = useDocumentParams();
  const { selectedOrganization } = useUser();
  const { data: pDocument } = useGetDocument(documentId, selectedOrganization);
  const [language, setLanguage] = useState(pDocument?.content.locale ?? 'sv-SE');
  const [texts, setTexts] = useState<Record<string, Record<string, string>>>({ [language]: defaultTexts });
  const { mutateAsync: save } = useDocumentMetadataSave();
  const [delayedSave] = useDelayedSave(save, 1000);

  const handleFreeTextChange = (key: string, newValue: string) => {
    if (!pDocument) return;
    const languageText = texts[language] || defaultTexts;
    const newLanguageText = { ...languageText, [key]: newValue };
    const newTexts = { ...texts, [language]: newLanguageText };
    const oldMetadata = pDocument.content.metadata;
    const { superId, revisionId } = pDocument;
    delayedSave({ documentSuperId: superId, documentRevisionId: revisionId, metadata: { ...oldMetadata, texts: newTexts } });

    setTexts(newTexts);
  };

  useEffect(() => {
    if (pDocument) {
      const currentTexts = pDocument.content?.metadata?.texts || {};
      const updatedTexts: Record<string, Record<string, string>> = { ...currentTexts };

      langs.forEach(({ locale }) => {
        if (!updatedTexts[locale]) {
          updatedTexts[locale] = { ...defaultTexts };
        }
      });

      setTexts(updatedTexts);
      setLanguage(pDocument.content.locale ?? 'sv-SE');
    }
  }, [pDocument]);

  return (
    <div>
      <h2 className="fs-1 mb-4">{t('metadata.free-texts')}</h2>
      <div className="my-5 w-25">
        <LanguageSelector locale={language} onChange={setLanguage} />
      </div>
      {Object.entries(texts[language] || defaultTexts).map(([key, value]) => (
        <div key={key} className="row mb-2">
          <label className="col-2 col-form-label">{i18nDynamic('project-details:metadata.prop.' + key)}</label>
          <div className="col-5 d-flex align-items-center">
            <input
              className="form-control"
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleFreeTextChange(key, e.target.value)}
              value={value as string}
              type="text"
            />
          </div>
        </div>
      ))}
      <hr className="my-4 border-gray-300" />
    </div>
  );
};

export default Metadata;
